import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneRight, ArrowRight } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { BRAND } from "./portalBrand";

const MODES = ["Sea-FCL", "Sea-LCL", "Air", "Road"];
const INCOTERMS = ["", "EXW", "FOB", "CIF", "CFR", "DAP", "DDP", "FCA"];
const EMPTY = { request_type: "Quote", mode: "Sea-FCL", origin: "", destination: "", cargo_type: "", equipment: "", incoterms: "", ready_date: "", commodity: "", weight_kg: "", volume_cbm: "", message: "" };

const statusChip = (s) => {
  const x = (s || "").toLowerCase();
  if (["won", "converted", "quoted"].some((k) => x.includes(k))) return "text-emerald-700 border-emerald-200 bg-emerald-50";
  if (["lost", "closed"].some((k) => x.includes(k))) return "text-slate-500 border-slate-200 bg-slate-100";
  if (x.includes("progress") || x.includes("contact")) return "text-amber-700 border-amber-200 bg-amber-50";
  return "text-blue-700 border-blue-200 bg-blue-50";
};

const lbl = "text-[11px] font-semibold uppercase tracking-wider text-slate-500";
const fld = "mt-1 w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

export default function PortalRequests() {
  const [form, setForm] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => portalApi.get("/portal/requests").then(({ data }) => setRows(data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.origin.trim() || !form.destination.trim()) { toast.error("Please enter origin and destination"); return; }
    setBusy(true);
    try {
      const payload = { ...form, weight_kg: form.weight_kg ? Number(form.weight_kg) : null, volume_cbm: form.volume_cbm ? Number(form.volume_cbm) : null };
      await portalApi.post("/portal/requests", payload);
      toast.success("Request sent — our team will get back to you shortly.");
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900">Request a Quote</h1>
        <p className="text-slate-500 text-sm mt-1">Tell us your lane and cargo — the {BRAND.name} team will respond with a rate. To place a shipment, use <span className="text-blue-700 font-medium">Book a Shipment</span>.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><label className={lbl}>Mode</label><select value={form.mode} onChange={(e) => set("mode", e.target.value)} className={fld}>{MODES.map((m) => <option key={m}>{m}</option>)}</select></div>
          <div><label className={lbl}>Origin (POL) *</label><input value={form.origin} onChange={(e) => set("origin", e.target.value)} className={fld} placeholder="e.g. Nhava Sheva" /></div>
          <div><label className={lbl}>Destination (POD) *</label><input value={form.destination} onChange={(e) => set("destination", e.target.value)} className={fld} placeholder="e.g. New York" /></div>
          <div><label className={lbl}>Commodity</label><input value={form.commodity} onChange={(e) => set("commodity", e.target.value)} className={fld} placeholder="What are you shipping?" /></div>
          <div><label className={lbl}>Cargo type</label><input value={form.cargo_type} onChange={(e) => set("cargo_type", e.target.value)} className={fld} placeholder="General / DG / Reefer…" /></div>
          <div><label className={lbl}>Equipment</label><input value={form.equipment} onChange={(e) => set("equipment", e.target.value)} className={fld} placeholder="20GP / 40HC / LCL…" /></div>
          <div><label className={lbl}>Incoterms</label><select value={form.incoterms} onChange={(e) => set("incoterms", e.target.value)} className={fld}>{INCOTERMS.map((i) => <option key={i} value={i}>{i || "—"}</option>)}</select></div>
          <div><label className={lbl}>Cargo ready date</label><input type="date" value={form.ready_date} onChange={(e) => set("ready_date", e.target.value)} className={fld} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={lbl}>Weight (kg)</label><input type="number" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Volume (cbm)</label><input type="number" value={form.volume_cbm} onChange={(e) => set("volume_cbm", e.target.value)} className={fld} /></div>
          </div>
        </div>
        <div>
          <label className={lbl}>Notes</label>
          <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className={fld + " h-auto py-2"} placeholder="Anything else we should know?" />
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold disabled:opacity-50">
            <PaperPlaneRight size={16} /> {busy ? "Sending…" : "Send quote request"}
          </button>
        </div>
      </form>

      <h2 className="text-sm font-semibold text-slate-700 mt-8 mb-3">Your requests</h2>
      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-10 text-center text-slate-500 text-sm">No requests yet. Submit one above.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border border-slate-200 bg-slate-50 text-slate-600">{r.request_type || "Quote"}</span>
                  <span className="text-sm text-slate-800 font-medium">{r.origin || "—"} <ArrowRight size={12} className="inline -mt-0.5 text-slate-400" /> {r.destination || "—"}</span>
                  <span className="text-xs text-slate-400">· {r.mode}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400">{(r.created_at || "").slice(0, 10)}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${statusChip(r.status)}`}>{r.status || "new"}</span>
                </div>
              </div>
              {r.message && <div className="text-xs text-slate-500 mt-2 whitespace-pre-wrap">{r.message}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
